import type { ReactNode } from 'react';

import { createContext, use } from 'react';

import type { ConditionMap } from '../conditions';

// ============================================================================
// Navigation Context Factory
// ============================================================================

/**
 * Creates a typed navigation context with Provider and hook.
 * Apps use this factory to create their own navigation context with domain-specific types.
 *
 * The context now includes an optional conditions map for evaluating named conditions
 * in route visibility. If conditions are provided, routes can use string condition names.
 *
 * @example
 * // In your app's navigation config:
 * type MyNavigationContext = {
 *     userRole: string;
 *     featureFlags: { darkMode: boolean };
 * };
 *
 * const myConditions = {
 *     canAccessAdmin: (ctx) => ctx.userRole === 'admin',
 *     canAccessDarkMode: (ctx) => ctx.featureFlags.darkMode,
 * };
 *
 * export const {
 *     NavigationProvider,
 *     useNavigationContext,
 *     useNavigationConditions,
 * } = createNavigationContext<MyNavigationContext>();
 */
export function createNavigationContext<TContext>() {
    const NavigationContextReact = createContext<TContext | null>(null);
    const NavigationConditionsReact = createContext<ConditionMap<TContext> | null>(null);

    type NavigationProviderProps = {
        value: TContext;
        /** Optional conditions map for named condition support */
        conditions?: ConditionMap<TContext>;
        children: ReactNode;
    };

    function NavigationProvider({ value, conditions, children }: NavigationProviderProps) {
        return (
            <NavigationContextReact value={value}>
                <NavigationConditionsReact value={conditions ?? null}>
                    {children}
                </NavigationConditionsReact>
            </NavigationContextReact>
        );
    }

    function useNavigationContext(): TContext {
        const context = use(NavigationContextReact);
        if (!context) {
            throw new Error('useNavigationContext must be used within a NavigationProvider');
        }
        return context;
    }

    /**
     * Returns the conditions map if one was provided to the NavigationProvider.
     * Returns an empty object if no conditions were provided.
     */
    function useNavigationConditions(): ConditionMap<TContext> {
        const conditions = use(NavigationConditionsReact);
        return conditions ?? {};
    }

    return {
        NavigationProvider,
        useNavigationContext,
        useNavigationConditions,
        /**
         * The raw React context, for advanced use cases.
         */
        NavigationContext: NavigationContextReact,
        /**
         * The raw conditions context, for advanced use cases.
         */
        NavigationConditionsContext: NavigationConditionsReact,
    };
}
